import { z } from 'zod'

/**
 * Length of a Cognito `ConfirmSignUp` / `ForgotPassword` confirmation code.
 *
 * Six is the pool default — `external-user-pool-stack.ts` uses the stock
 * `{####}` placeholder and configures no custom length. Deliberately *not*
 * shared with `SendOTPForm`'s `CODE_LENGTH = 8`, which is the custom-auth
 * OTP sign-in code: a different code, from a different flow.
 */
export const CONFIRMATION_CODE_LENGTH = 6

/** Derived from the length so the two can never drift apart. */
export const CONFIRMATION_CODE_PATTERN = new RegExp(
  `^\\d{${CONFIRMATION_CODE_LENGTH}}$`,
)

export const CONFIRMATION_CODE_MESSAGE = `Enter the ${CONFIRMATION_CODE_LENGTH}-digit code from your email`

/**
 * Reduce anything a user can put in the field to a valid code prefix: digits
 * only, at most {@link CONFIRMATION_CODE_LENGTH} of them.
 *
 * The verification email reads "Your verification code is 123456", and a
 * drag-select routinely grabs a leading space or a trailing newline with it.
 * Leading zeros survive — this is string work, never `Number()`. (#2882)
 *
 * @example
 * sanitiseConfirmationCode(' 123456\n') // '123456'
 * sanitiseConfirmationCode('012345')    // '012345'
 */
export function sanitiseConfirmationCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, CONFIRMATION_CODE_LENGTH)
}

/**
 * The form-level rule for a confirmation code. Six digits is the real
 * constraint, and saying so beats a Cognito `CodeMismatchException`
 * round-trip to tell the user they typed five.
 */
export const confirmationCodeSchema = z
  .string()
  .regex(CONFIRMATION_CODE_PATTERN, CONFIRMATION_CODE_MESSAGE)
